/**
 * PendingInvitationsCard (GH #2307 deliverable 2)
 *
 * FI-admin surface for invitations that have been sent but not yet accepted.
 * Consumes the three invitation hooks that shipped in Sprint 5 with no
 * consumer anywhere in the repo: `useInvitations`, `useResendInvitation`,
 * `useRevokeInvitation` (src/hooks/useStaffLifecycle.ts:298, :340, :325).
 *
 * Why this surface has to exist: `invite()` writes ONLY an Invitation row -- no
 * TenantUser is created until the invitee accepts (acceptInvitation creates it
 * directly as ACTIVE). So an outstanding invitation appears NOWHERE on the
 * staff roster, and an FI admin who mistypes an address, or whose invitee never
 * received the email, previously had no way to see the pending grant at all,
 * let alone resend or withdraw it. Both are institution access-control
 * decisions and belong to the FI admin, not to Aaim support.
 *
 * Scope: PENDING invitations only. ACCEPTED invitations are represented by
 * the staff roster row; REVOKED and EXPIRED are terminal and carry no
 * action, so surfacing them here would be a list of dead controls.
 *
 * Composes @aaimplatform/ui primitives (Card, Heading, Table, Button,
 * Badge, Alert, EmptyState). No local re-implementation, no colored
 * left rail, no fabricated placeholder rows.
 */

import { useState } from 'react';
import { MailCheck as InvitationIcon, MailX as NoInvitationsIcon } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Table,
  type TableColumn,
} from '@aaimplatform/ui';
import {
  useInvitations,
  useResendInvitation,
  useRevokeInvitation,
  type Invitation,
} from '../../hooks/useStaffLifecycle';

/**
 * Role slugs are FI-type-neutral by mandate: an institution is a credit
 * union, community bank, regional bank, broker-dealer, trust company, or
 * any other canonical InstitutionType. The label mirrors the slug, it does
 * not editorialize about charter type.
 */
const ROLE_LABELS: Record<string, string> = {
  fi_admin: 'FI Admin',
  fi_loan_officer: 'FI Loan Officer',
  fi_compliance: 'FI Compliance',
  fi_ops: 'FI Operations',
  pledge_reviewer: 'Pledge Reviewer',
};

export function formatRoleSlug(slug: string): string {
  return ROLE_LABELS[slug] ?? slug;
}

function formatDate(value?: string | null): string {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/** True when the acceptance deadline has already passed on the client clock. */
export function isPastDue(expiresAt: string, now: Date = new Date()): boolean {
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return false;
  return expiry.getTime() < now.getTime();
}

export interface PendingInvitationsCardProps {
  /**
   * Invoked after a resend or revoke resolves so the parent can refetch the
   * staff roster. Neither action changes a roster row (an outstanding
   * invitation has no TenantUser yet), but refetching keeps the two surfaces
   * consistent if the invitee accepts between renders.
   */
  onInvitationChanged?: () => void;
}

/**
 * PendingInvitationsCard -- outstanding invitations with resend and revoke.
 */
export function PendingInvitationsCard({
  onInvitationChanged,
}: PendingInvitationsCardProps) {
  const invitationsQuery = useInvitations({ status: 'PENDING' });
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();

  // Revoke is destructive (it withdraws an access grant), so the row asks
  // for a second click rather than firing on the first.
  const [confirmingRevokeId, setConfirmingRevokeId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const invitations = invitationsQuery.data?.items ?? [];
  const total = invitationsQuery.data?.total ?? invitations.length;

  const handleResend = (invitation: Invitation) => {
    setNotice(null);
    setActionError(null);
    resend.mutate(
      { invitationId: invitation.id },
      {
        onSuccess: () => {
          // A resend RE-ISSUES: the backend revokes the stale invitation and mints a
          // fresh token with a fresh expiry, because only the token hash is stored
          // and the original link is unrecoverable by design. Say that, so an admin
          // knows the previously-sent link stops working.
          setNotice(
            `New invitation sent to ${invitation.email}. Any earlier link for this person no longer works.`,
          );
          onInvitationChanged?.();
        },
        onError: (err) => {
          setActionError(
            err instanceof Error
              ? err.message
              : `Could not re-send the invitation to ${invitation.email}.`,
          );
        },
      },
    );
  };

  const handleRevoke = (invitation: Invitation) => {
    setNotice(null);
    setActionError(null);
    revoke.mutate(
      { invitationId: invitation.id },
      {
        onSuccess: () => {
          setConfirmingRevokeId(null);
          setNotice(`Invitation for ${invitation.email} revoked.`);
          onInvitationChanged?.();
        },
        onError: (err) => {
          setConfirmingRevokeId(null);
          setActionError(
            err instanceof Error
              ? err.message
              : `Could not revoke the invitation for ${invitation.email}.`,
          );
        },
      },
    );
  };

  const columns: TableColumn<Invitation>[] = [
    {
      key: 'email',
      label: 'Invitee',
      render: (_value, row) => (
        <span className="text-sm font-mono text-primary">{row.email}</span>
      ),
    },
    {
      key: 'roleSlug',
      label: 'Role on acceptance',
      render: (_value, row) => (
        <span className="text-sm font-sans text-secondary">
          {formatRoleSlug(row.roleSlug)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_value, row) =>
        isPastDue(row.expiresAt) ? (
          <Badge variant="warning" size="small">Past due</Badge>
        ) : (
          <Badge variant="warning" size="small">Pending</Badge>
        ),
    },
    {
      key: 'expiresAt',
      label: 'Accept by',
      render: (_value, row) => (
        <span className="text-sm font-sans text-secondary">
          {formatDate(row.expiresAt)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Sent',
      render: (_value, row) => (
        <span className="text-sm font-sans text-tertiary">
          {formatDate(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (_value, row) => {
        const busy = resend.isPending || revoke.isPending;
        if (confirmingRevokeId === row.id) {
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="text-xs font-sans text-secondary">
                Withdraw this access grant?
              </span>
              <Button
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={() => handleRevoke(row)}
              >
                Confirm revoke
              </Button>
              <Button
                variant="tertiary"
                size="sm"
                disabled={busy}
                onClick={() => setConfirmingRevokeId(null)}
              >
                Keep
              </Button>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => handleResend(row)}
            >
              Resend
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setNotice(null);
                setActionError(null);
                setConfirmingRevokeId(row.id);
              }}
            >
              Revoke
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <Card>
      <div className="px-6 py-4 border-b border-recessed flex items-center gap-2">
        <InvitationIcon size={18} className="text-accent" aria-hidden />
        <Heading level={2} size="subsection">
          Outstanding invitations
        </Heading>
        <span className="ml-2 text-xs font-mono text-tertiary">
          {invitationsQuery.isFetching ? '...' : total}
        </span>
      </div>

      <div className="p-4">
        {notice && (
          <div className="mb-4">
            <Alert type="success" title="Invitation updated">
              {notice}
            </Alert>
          </div>
        )}

        {actionError && (
          <div className="mb-4">
            <Alert type="error" title="Action failed">
              {actionError}
            </Alert>
          </div>
        )}

        {invitationsQuery.isError ? (
          <Alert type="error" title="Unable to load invitations">
            {invitationsQuery.error?.message ??
              'Outstanding invitations could not be loaded.'}
          </Alert>
        ) : invitationsQuery.isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div
              role="status"
              aria-label="Loading invitations"
              className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"
            />
          </div>
        ) : invitations.length === 0 ? (
          <EmptyState
            icon={<NoInvitationsIcon className="h-8 w-8" aria-hidden />}
            title="No outstanding invitations"
            description="Every invitation your institution has sent has been accepted, revoked, or has expired. Use [Invite staff] to grant access to another person."
          />
        ) : (
          <Table columns={columns} data={invitations} hoverable={false} />
        )}
      </div>
    </Card>
  );
}

export default PendingInvitationsCard;
